export type DocStatus =
  | 'pendente'
  | 'gerando'
  | 'rascunho'
  | 'revisao'
  | 'aprovado'
  | 'rejeitado';

interface DocCircleProps {
  status: DocStatus;
  /** Tooltip (default: usa o próprio status) */
  title?: string;
}

/**
 * Mapa status → ícone unicode.
 * Os símbolos transmitem informação adicional além da cor:
 * - `gerando` (◉) sugere movimento (combina com animação pulse)
 * - `rejeitado` (✕) comunica negativo
 * - `aprovado` (●) cheio = completo
 */
const DOC_ICONE: Record<DocStatus, string> = {
  pendente:  '○',
  gerando:   '◉',
  rascunho:  '◐',
  revisao:   '◑',
  aprovado:  '●',
  rejeitado: '✕',
};

const STATUS_CLASSES: Record<DocStatus, string> = {
  pendente:  'bg-transparent border-[1.5px] border-[var(--gray-300)] text-[var(--gray-400)]',
  gerando:   'bg-[var(--ai-500)] text-white animate-doc-pulse',
  rascunho:  'bg-[var(--warning-500)] text-white',
  revisao:   'bg-[var(--brand-600)] text-white',
  aprovado:  'bg-[var(--success-500)] text-white',
  rejeitado: 'bg-[var(--error-500)] text-white',
};

const STATUS_LABEL: Record<DocStatus, string> = {
  pendente:  'pendente',
  gerando:   'gerando',
  rascunho:  'rascunho',
  revisao:   'em revisão',
  aprovado:  'aprovado',
  rejeitado: 'rejeitado',
};

/**
 * Indicador circular de status de documento (14x14px) com ícone unicode interno.
 *
 * @example
 * <DocCircle status="aprovado" />
 * <DocCircle status="gerando" title="IA gerando ETP..." />
 */
export default function DocCircle({ status, title }: DocCircleProps) {
  const icone = DOC_ICONE[status];
  const cls = STATUS_CLASSES[status];
  const label = title ?? `Status: ${STATUS_LABEL[status]}`;

  return (
    <span
      className={`inline-flex size-[14px] shrink-0 items-center justify-center rounded-full text-[9px] leading-none ${cls}`}
      role="img"
      aria-label={label}
      title={label}
    >
      {icone}
    </span>
  );
}
